import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { AuthService } from '../../core/services/auth.service';
import { APPLICATION_ACTIONS, AUTH_ACTIONS, CLAIMS_API_ACTIONS } from '../actions';
import { filter, map, of, switchMap, tap } from 'rxjs';
import { ConfirmationService } from 'primeng/api';
import { Store } from '@ngrx/store';
import { AUTH_REDUCER } from '../reducers/auth.reducer';

@Injectable()
export class AuthEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly authService: AuthService,
    private readonly confirmService: ConfirmationService,
    private readonly store: Store,
  ) {}

  login$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AUTH_ACTIONS.login),
        tap(() => this.authService.login()),
      );
    },
    { dispatch: false },
  );

  initLoginCheck$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AUTH_ACTIONS.initLoginCheck, AUTH_ACTIONS.login),
      map(() => APPLICATION_ACTIONS.showLoader()),
    );
  });

  // on authorized hook
  authorized$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AUTH_ACTIONS.authenticated),
      switchMap(() => of(APPLICATION_ACTIONS.hideLoader(), CLAIMS_API_ACTIONS.get())),
    );
  });

  sessionTerminated$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AUTH_ACTIONS.sessionTerminated),
        tap(() => {
          this.confirmService.confirm({
            header: 'Confirmation',
            message: 'Your session has expired. Please sign in again.',
            rejectVisible: false,
            acceptLabel: 'Ok',
            accept: () => window.location.reload(),
          });
        }),
      );
    },
    { dispatch: false },
  );

  logout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AUTH_ACTIONS.logout),
      tap(() => this.authService.logout()),
      map(() => APPLICATION_ACTIONS.showLoader()),
    );
  });

  pushUserInfoToDataLayer$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AUTH_ACTIONS.userProfileLoaded),
        concatLatestFrom(() => this.store.select(AUTH_REDUCER.selectUserProfile)),
        filter(([, user]) => !!user['user-id'] && !!user.preferred_username),
        tap(([, user]) => {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'userAuthenticated',
            userId: `F${user['user-id']}`,
            userName: user.preferred_username,
          });
        }),
      );
    },
    { dispatch: false },
  );
}
